import { OrderType, OrderWarning } from '@/types/graphql';
import { gql } from '@apollo/client';
import { CheckCircle, DoNotDisturbOn, PauseCircle, WarningRounded } from '@mui/icons-material';
import { Tooltip, Typography, TypographyProps } from '@mui/material';
import moment from 'moment';
import { tz } from 'moment-timezone';

import { AXLE_TEAL } from '../../constants/colors';
import { isOrderDiscontinued } from '../../types/order';
import { formatNullableDatetime } from '../../utils/datetimes/formatting';
import { CaptionLabel } from '../labels/CaptionLabel';
import { HorizontalStack, VerticalStack } from '../utils/linearLayouts';
import { renderOrderMinMaxActiveOccurrences } from './labels';

export const OrderStatusFragment = gql`
    fragment OrderStatusFragment on Order {
        id
        isComplete
        discontinuedAt
        isPaused
        orderWarnings {
            type
            message
        }
        orderType
        numActiveOccurrences
        maxActiveOccurrences
        minActiveOccurrences
    }
`;

export interface IOrderStatusFragment {
    id: string;
    isComplete: boolean;
    discontinuedAt: Date | null;
    isPaused: boolean;
    orderWarnings: {
        type: OrderWarning;
        message: string;
    }[];
    orderType: OrderType;
    numActiveOccurrences: number;
    maxActiveOccurrences: number;
    minActiveOccurrences: number;
    timezone: string | null;
}

export const OrderStatusIcon = ({
    order: { isComplete, discontinuedAt, isPaused, orderType, orderWarnings, timezone },
}: {
    order: IOrderStatusFragment;
}) => {
    const warnings =
        orderWarnings.length > 0 ? (
            <VerticalStack>{orderWarnings.map((warning) => warning.message)}</VerticalStack>
        ) : null;
    if (isPaused) {
        return (
            <Tooltip
                title={
                    <VerticalStack>
                        <Typography>Paused</Typography>
                        {warnings}
                    </VerticalStack>
                }
            >
                <PauseCircle
                    color={orderWarnings.length > 0 ? 'warning' : 'action'}
                    sx={{
                        fontSize: '1.5rem',
                    }}
                />
            </Tooltip>
        );
    }
    if (isOrderDiscontinued(discontinuedAt)) {
        const discontinuedLabel = `Discontinued ${formatNullableDatetime(
            moment(discontinuedAt),
            timezone ?? tz.guess(),
        )}`;
        return (
            <Tooltip
                title={
                    <VerticalStack>
                        {discontinuedLabel}
                        {warnings}
                    </VerticalStack>
                }
            >
                <DoNotDisturbOn
                    color={orderWarnings.length > 0 ? 'warning' : 'action'}
                    sx={{
                        fontSize: '1.5rem',
                    }}
                />
            </Tooltip>
        );
    }
    if (isComplete && orderType === OrderType.FREQUENCY) {
        return <CheckCircle sx={{ fontSize: '1.5rem', color: AXLE_TEAL }} />;
    }
    if (orderWarnings.length > 0) {
        return (
            <Tooltip title={warnings}>
                <WarningRounded color='warning' sx={{ fontSize: '1.5rem' }} />
            </Tooltip>
        );
    }

    return null;
};

export const OrderStatusDisplay = ({
    order,
    typographyProps = {},
    label = true,
}: {
    order: IOrderStatusFragment;
    typographyProps?: TypographyProps;
    label?: boolean;
}) => {
    const { orderType, numActiveOccurrences, maxActiveOccurrences, minActiveOccurrences } = order;
    return (
        <VerticalStack>
            {label ? <CaptionLabel>Status</CaptionLabel> : null}
            <HorizontalStack spacing={1} width='auto'>
                <OrderStatusIcon order={order} />
                <Typography {...typographyProps}>
                    {orderType === OrderType.FREQUENCY
                        ? renderOrderMinMaxActiveOccurrences({
                              numActiveOccurrences,
                              maxActiveOccurrences,
                              minActiveOccurrences,
                          })
                        : `${numActiveOccurrences} / ${maxActiveOccurrences} PRN visits`}
                </Typography>
            </HorizontalStack>
        </VerticalStack>
    );
};
